import { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { Users, Search, Plus, Eye, Phone, Mail, Building, Calendar, Droplets, X, ChevronDown, ChevronUp, Upload, FileText, Trash2, Pencil, FileSpreadsheet, Camera } from 'lucide-react';
import { canUserEdit, canUserDelete } from '@/utils/permissions';

const INITIAL_FORM = {
  name: '', gender: '', dateOfBirth: '', placeOfBirth: '',
  joinDate: '', designation: '', department: '', function: '', role: '',
  identificationMark: '',
  currentAddress: '', telephone: '', mobile: '',
  permanentAddress: '', addressPhone: '', email: '',
  passportNo: '', passportPlaceOfIssue: '', passportDateOfExpiry: '',
  drivingLicenseNo: '',
  modeOfTravel: '', vehicleRegNo: '',
  bankAccountNo: '', ifscCode: '', esiNo: '', epfNo: '',
  lastEmployer: '', lastEmployerContact: '', lastGrossSalary: '',
  lastQualification: '', lastEducationalInstitution: '', yearOfGraduation: '',
  fatherName: '', fatherAddress: '', fatherContact: '',
  motherName: '', motherAddress: '', motherContact: '',
  married: 'No', spouseName: '', spouseAddress: '', spouseContact: '',
  bloodGroup: '', knownAllergies: '',
  emergencyContact: '', emergencyContactNumber: '',
  reference: '',
  salaryBasic: '', salaryHRA: '', salaryDA: '', salaryOtherAllowance: '', salaryIncentive: '',
  employeeId: '', status: 'Active', photo: ''
};

const departments = ['Production', 'Raw Material', 'Dispatch', 'Stores', 'Maintenance', 'HR', 'Admin', 'Quality', 'Accounts'];
const bloodGroups = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}{required && ' *'}</label>
    {children}
  </div>
);

const inputCls = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1a237e] focus:border-[#1a237e] outline-none";
const selectCls = inputCls;
const readOnlyCls = "w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-700";

const SectionHeader = ({ title, open, toggle }) => (
  <button type="button" onClick={toggle} className="w-full flex items-center justify-between py-2 border-b border-gray-200 mb-3">
    <h3 className="text-sm font-bold text-[#1a237e] uppercase tracking-wide">{title}</h3>
    {open ? <ChevronUp size={16} className="text-gray-500" /> : <ChevronDown size={16} className="text-gray-500" />}
  </button>
);

function MasterRoll() {
  const { API, user } = useContext(AppContext);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [formData, setFormData] = useState({ ...INITIAL_FORM });
  const [openSections, setOpenSections] = useState({
    personal: true, contact: true, documents: false, travel: false,
    prevEmployment: false, family: false, health: true, reference: false, salary: false, uploads: false
  });
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [editMode, setEditMode] = useState(false);
  const [editingEmployeeId, setEditingEmployeeId] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const bulkFileRef = useRef(null);

  useEffect(() => { fetchEmployees(); }, []);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/hr/employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    const newDocs = [];
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) { alert(`"${file.name}" exceeds 10MB limit.`); continue; }
      const data = await new Promise(resolve => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve(ev.target.result);
        reader.readAsDataURL(file);
      });
      newDocs.push({ id: Date.now() + '_' + Math.random().toString(36).substr(2, 6), name: file.name, type: file.type, size: file.size, data, uploadedAt: new Date().toISOString() });
    }
    setUploadedDocs(prev => [...prev, ...newDocs]);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeDoc = (id) => setUploadedDocs(prev => prev.filter(d => d.id !== id));

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Auto-calculated salary fields
  const salaryCalc = useMemo(() => {
    const basic = parseFloat(formData.salaryBasic) || 0;
    const hra = parseFloat(formData.salaryHRA) || 0;
    const da = parseFloat(formData.salaryDA) || 0;
    const otherAllowance = parseFloat(formData.salaryOtherAllowance) || 0;
    const incentive = parseFloat(formData.salaryIncentive) || 0;
    const bonus = Math.round((basic + hra + da + otherAllowance) * 0.0833 * 100) / 100;
    const grossMonth = basic + hra + da + otherAllowance + incentive + bonus;
    const grossAnnum = grossMonth * 12;
    return { bonus, grossMonth, grossAnnum };
  }, [formData.salaryBasic, formData.salaryHRA, formData.salaryDA, formData.salaryOtherAllowance, formData.salaryIncentive]);

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        lastGrossSalary: parseFloat(formData.lastGrossSalary) || 0,
        salaryBasic: parseFloat(formData.salaryBasic) || 0,
        salaryHRA: parseFloat(formData.salaryHRA) || 0,
        salaryDA: parseFloat(formData.salaryDA) || 0,
        salaryOtherAllowance: parseFloat(formData.salaryOtherAllowance) || 0,
        salaryIncentive: parseFloat(formData.salaryIncentive) || 0,
        salaryBonus: salaryCalc.bonus,
        grossPerAnnum: salaryCalc.grossAnnum,
        grossPerMonth: salaryCalc.grossMonth,
        photo: formData.photo || '',
        documents: uploadedDocs,
      };
      if (editMode && editingEmployeeId) {
        await axios.put(`${API}/hr/employees/${editingEmployeeId}`, payload);
      } else {
        await axios.post(`${API}/hr/employees`, payload);
      }
      setShowAddModal(false);
      setFormData({ ...INITIAL_FORM });
      setUploadedDocs([]);
      setEditMode(false);
      setEditingEmployeeId(null);
      fetchEmployees();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save employee');
    }
  };

  const handleEditEmployee = async (employee) => {
    try {
      const response = await axios.get(`${API}/hr/employees/${employee.id}`);
      const emp = response.data;
      setFormData({
        name: emp.name || '', gender: emp.gender || '', dateOfBirth: emp.dateOfBirth || '', placeOfBirth: emp.placeOfBirth || '',
        joinDate: emp.joinDate || '', designation: emp.designation || '', department: emp.department || '', function: emp.function || '', role: emp.role || '',
        identificationMark: emp.identificationMark || '',
        currentAddress: emp.currentAddress || '', telephone: emp.telephone || '', mobile: emp.mobile || '',
        permanentAddress: emp.permanentAddress || '', addressPhone: emp.addressPhone || '', email: emp.email || '',
        passportNo: emp.passportNo || '', passportPlaceOfIssue: emp.passportPlaceOfIssue || '', passportDateOfExpiry: emp.passportDateOfExpiry || '',
        drivingLicenseNo: emp.drivingLicenseNo || '',
        modeOfTravel: emp.modeOfTravel || '', vehicleRegNo: emp.vehicleRegNo || '',
        bankAccountNo: emp.bankAccountNo || '', ifscCode: emp.ifscCode || '', esiNo: emp.esiNo || '', epfNo: emp.epfNo || '',
        lastEmployer: emp.lastEmployer || '', lastEmployerContact: emp.lastEmployerContact || '', lastGrossSalary: emp.lastGrossSalary || '',
        lastQualification: emp.lastQualification || '', lastEducationalInstitution: emp.lastEducationalInstitution || '', yearOfGraduation: emp.yearOfGraduation || '',
        fatherName: emp.fatherName || '', fatherAddress: emp.fatherAddress || '', fatherContact: emp.fatherContact || '',
        motherName: emp.motherName || '', motherAddress: emp.motherAddress || '', motherContact: emp.motherContact || '',
        married: emp.married || 'No', spouseName: emp.spouseName || '', spouseAddress: emp.spouseAddress || '', spouseContact: emp.spouseContact || '',
        bloodGroup: emp.bloodGroup || '', knownAllergies: emp.knownAllergies || '',
        emergencyContact: emp.emergencyContact || '', emergencyContactNumber: emp.emergencyContactNumber || '',
        reference: emp.reference || '',
        salaryBasic: emp.salaryBasic || '', salaryHRA: emp.salaryHRA || '', salaryDA: emp.salaryDA || '', salaryOtherAllowance: emp.salaryOtherAllowance || '', salaryIncentive: emp.salaryIncentive || '',
        employeeId: emp.employeeId || '', status: emp.status || 'Active', photo: emp.photo || ''
      });
      setUploadedDocs(emp.documents || []);
      setEditMode(true);
      setEditingEmployeeId(employee.id);
      setShowAddModal(true);
    } catch (error) {
      alert('Failed to load employee data for editing');
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBulkUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const response = await axios.post(`${API}/hr/employees/bulk-upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      alert(`Bulk upload complete: ${response.data.inserted} employees added, ${response.data.skipped} skipped.`);
      fetchEmployees();
    } catch (error) {
      alert('Bulk upload failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setBulkUploading(false);
      if (bulkFileRef.current) bulkFileRef.current.value = '';
    }
  };

  const handleDeleteEmployee = async (employee) => {
    if (!window.confirm(`Are you sure you want to delete ${employee.name}? This action cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/hr/employees/${employee.id}`);
      fetchEmployees();
    } catch (error) {
      alert('Failed to delete employee: ' + (error.response?.data?.detail || error.message));
    }
  };

  const viewEmployeeDetails = async (employeeId) => {
    try {
      const response = await axios.get(`${API}/hr/employees/${employeeId}`);
      setSelectedEmployee(response.data);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Error fetching employee details:', error);
      alert('Unable to load employee profile. Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const filteredEmployees = employees.filter(emp => {
    const matchesSearch = emp.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         emp.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         emp.employeeId?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDept = departmentFilter === 'all' || emp.department === departmentFilter;
    const matchesStatus = statusFilter === 'all' || emp.status === statusFilter;
    return matchesSearch && matchesDept && matchesStatus;
  });

  const canEdit = canUserEdit(user, '/hr/master-roll');
  const canDelete = canUserDelete(user, '/hr/master-roll');
  const toggleSection = (s) => setOpenSections(prev => ({ ...prev, [s]: !prev[s] }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a237e]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="master-roll-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="text-[#1a237e]" size={28} />
            Employee Directory
          </h1>
          <p className="text-gray-600 mt-1">Complete employee records and profiles</p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <input ref={bulkFileRef} type="file" accept=".xlsx,.xls" onChange={handleBulkUpload} className="hidden" data-testid="bulk-upload-input" />
            <button
              onClick={() => bulkFileRef.current?.click()}
              disabled={bulkUploading}
              className="flex items-center gap-2 px-4 py-2 border border-[#1a237e] text-[#1a237e] rounded-lg hover:bg-[#1a237e]/10 transition"
              data-testid="bulk-upload-btn"
            >
              <FileSpreadsheet size={18} />
              {bulkUploading ? 'Uploading...' : 'Bulk Upload'}
            </button>
            <button
              onClick={() => { setEditMode(false); setEditingEmployeeId(null); setFormData({ ...INITIAL_FORM }); setUploadedDocs([]); setShowAddModal(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-[#1a237e] text-white rounded-lg hover:bg-[#283593] transition"
              data-testid="add-employee-btn"
            >
              <Plus size={18} />
              Add Employee
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search by name, email, or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a237e] focus:border-[#1a237e] outline-none"
              data-testid="search-input"
            />
          </div>
          <select value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a237e] outline-none" data-testid="department-filter">
            <option value="all">All Departments</option>
            {departments.map(dept => <option key={dept} value={dept}>{dept}</option>)}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a237e] outline-none" data-testid="status-filter">
            <option value="all">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
          <div className="text-sm text-gray-600 flex items-center">
            Showing {filteredEmployees.length} of {employees.length} employees
          </div>
        </div>
      </div>

      {/* Employee Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredEmployees.map((employee) => (
          <div key={employee.id} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-lg hover:border-[#1a237e]/30 transition" data-testid={`employee-card-${employee.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                {employee.photo ? (
                  <img src={employee.photo} alt={employee.name} className="h-12 w-12 rounded-full object-cover border-2 border-[#1a237e]/20" />
                ) : (
                  <div className="h-12 w-12 bg-gradient-to-br from-[#1a237e] to-[#3949ab] rounded-full flex items-center justify-center text-white font-bold text-lg">
                    {employee.name?.charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <h3 className="font-semibold text-gray-900">{employee.name}</h3>
                  <p className="text-sm text-[#e65100]">{employee.designation || 'Employee'}</p>
                </div>
              </div>
              <span className={`px-2 py-1 text-xs rounded-full ${employee.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                {employee.status}
              </span>
            </div>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex items-center gap-2 text-gray-600"><Building size={14} /><span>{employee.department || 'Not Assigned'}</span></div>
              {employee.email && <div className="flex items-center gap-2 text-gray-600"><Mail size={14} /><span className="truncate">{employee.email}</span></div>}
              {employee.mobile && <div className="flex items-center gap-2 text-gray-600"><Phone size={14} /><span>{employee.mobile}</span></div>}
              {employee.joinDate && <div className="flex items-center gap-2 text-gray-600"><Calendar size={14} /><span>Joined: {employee.joinDate}</span></div>}
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center">
              <span className="text-xs text-gray-500 font-mono">{employee.employeeId || employee.id}</span>
              <div className="flex items-center gap-2">
                {canEdit && canDelete && (
                  <button onClick={() => handleDeleteEmployee(employee)}
                    className="flex items-center gap-1 text-red-600 hover:bg-red-50 px-3 py-1 rounded-lg transition text-sm font-medium" data-testid={`delete-employee-${employee.id}`}>
                    <Trash2 size={14} /> Delete
                  </button>
                )}
                {canEdit && (
                  <button onClick={() => handleEditEmployee(employee)}
                    className="flex items-center gap-1 text-[#e65100] hover:bg-[#e65100]/10 px-3 py-1 rounded-lg transition text-sm font-medium" data-testid={`edit-employee-${employee.id}`}>
                    <Pencil size={14} /> Edit
                  </button>
                )}
                <button onClick={() => viewEmployeeDetails(employee.id)}
                  className="flex items-center gap-1 text-[#1a237e] hover:bg-[#1a237e]/10 px-3 py-1 rounded-lg transition text-sm font-medium" data-testid={`view-employee-${employee.id}`}>
                  <Eye size={14} /> View Profile
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredEmployees.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <Users className="mx-auto text-gray-400 mb-3" size={48} />
          <p className="text-gray-500">No employees found matching your criteria</p>
        </div>
      )}

      {/* ===== ADD EMPLOYEE MODAL ===== */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto">
            <div className="sticky top-0 bg-white z-10 p-5 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{editMode ? 'Edit Employee' : 'Add New Employee'}</h2>
                <p className="text-xs text-gray-500 mt-0.5">{editMode ? 'Update the employee details below.' : 'Fill in the details below. Fields marked * are required.'}</p>
              </div>
              <button onClick={() => { setShowAddModal(false); setFormData({ ...INITIAL_FORM }); setEditMode(false); setEditingEmployeeId(null); }} className="p-2 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
            </div>
            <form onSubmit={handleAddEmployee} className="p-5 space-y-4">

              {/* PHOTO UPLOAD */}
              <div className="flex items-center gap-5 pb-4 border-b border-gray-200">
                <div className="relative group">
                  {formData.photo ? (
                    <img src={formData.photo} alt="Employee" className="h-20 w-20 rounded-full object-cover border-2 border-[#1a237e]/30" />
                  ) : (
                    <div className="h-20 w-20 bg-gradient-to-br from-[#1a237e] to-[#3949ab] rounded-full flex items-center justify-center text-white">
                      <Camera size={28} />
                    </div>
                  )}
                  <label className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 cursor-pointer transition">
                    <Camera size={20} className="text-white" />
                    <input type="file" accept="image/*" className="hidden" data-testid="photo-upload-input"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > 2 * 1024 * 1024) { alert('Photo must be under 2MB'); return; }
                        const reader = new FileReader();
                        reader.onload = (ev) => set('photo', ev.target.result);
                        reader.readAsDataURL(file);
                      }} />
                  </label>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Employee Photo</p>
                  <p className="text-xs text-gray-400">Click to upload (max 2MB, JPG/PNG)</p>
                  {formData.photo && (
                    <button type="button" onClick={() => set('photo', '')} className="text-xs text-red-500 hover:underline mt-1">Remove photo</button>
                  )}
                </div>
              </div>

              {/* PERSONAL INFORMATION */}
              <SectionHeader title="Personal Information" open={openSections.personal} toggle={() => toggleSection('personal')} />
              {openSections.personal && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <Field label="Employee ID"><input type="text" value={formData.employeeId} onChange={(e) => set('employeeId', e.target.value)} placeholder="EMP-001" className={inputCls} data-testid="add-emp-id" /></Field>
                  <Field label="Name" required><input type="text" required value={formData.name} onChange={(e) => set('name', e.target.value)} className={inputCls} data-testid="add-emp-name" /></Field>
                  <Field label="Gender">
                    <select value={formData.gender} onChange={(e) => set('gender', e.target.value)} className={selectCls} data-testid="add-emp-gender">
                      <option value="">Select</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </Field>
                  <Field label="Date of Birth"><input type="date" value={formData.dateOfBirth} onChange={(e) => set('dateOfBirth', e.target.value)} className={inputCls} data-testid="add-emp-dob" /></Field>
                  <Field label="Place of Birth"><input type="text" value={formData.placeOfBirth} onChange={(e) => set('placeOfBirth', e.target.value)} className={inputCls} /></Field>
                  <Field label="Date of Joining" required><input type="date" required value={formData.joinDate} onChange={(e) => set('joinDate', e.target.value)} className={inputCls} data-testid="add-emp-doj" /></Field>
                  <Field label="Designation" required><input type="text" required value={formData.designation} onChange={(e) => set('designation', e.target.value)} className={inputCls} data-testid="add-emp-designation" /></Field>
                  <Field label="Department" required>
                    <select required value={formData.department} onChange={(e) => set('department', e.target.value)} className={selectCls} data-testid="add-emp-dept">
                      <option value="">Select Department</option>
                      {departments.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </Field>
                  <Field label="Function"><input type="text" value={formData.function} onChange={(e) => set('function', e.target.value)} placeholder="e.g., Furnace Operations" className={inputCls} /></Field>
                  <Field label="Role"><input type="text" value={formData.role} onChange={(e) => set('role', e.target.value)} placeholder="e.g., Operator" className={inputCls} /></Field>
                  <Field label="Identification Mark"><input type="text" value={formData.identificationMark} onChange={(e) => set('identificationMark', e.target.value)} className={inputCls} /></Field>
                  <Field label="Status">
                    <select value={formData.status} onChange={(e) => set('status', e.target.value)} className={selectCls}>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </Field>
                </div>
              )}

              {/* CONTACT INFORMATION */}
              <SectionHeader title="Contact Information" open={openSections.contact} toggle={() => toggleSection('contact')} />
              {openSections.contact && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <Field label="Current Address"><input type="text" value={formData.currentAddress} onChange={(e) => set('currentAddress', e.target.value)} className={inputCls} /></Field>
                  <Field label="Telephone"><input type="tel" value={formData.telephone} onChange={(e) => set('telephone', e.target.value)} className={inputCls} /></Field>
                  <Field label="Mobile"><input type="tel" value={formData.mobile} onChange={(e) => set('mobile', e.target.value)} className={inputCls} data-testid="add-emp-mobile" /></Field>
                  <Field label="Permanent Address"><input type="text" value={formData.permanentAddress} onChange={(e) => set('permanentAddress', e.target.value)} className={inputCls} /></Field>
                  <Field label="Address Phone"><input type="tel" value={formData.addressPhone} onChange={(e) => set('addressPhone', e.target.value)} className={inputCls} /></Field>
                  <Field label="Email Address"><input type="email" value={formData.email} onChange={(e) => set('email', e.target.value)} className={inputCls} data-testid="add-emp-email" /></Field>
                </div>
              )}

              {/* DOCUMENTS */}
              <SectionHeader title="Documents" open={openSections.documents} toggle={() => toggleSection('documents')} />
              {openSections.documents && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <Field label="Passport No"><input type="text" value={formData.passportNo} onChange={(e) => set('passportNo', e.target.value)} className={inputCls} /></Field>
                  <Field label="Place of Issue"><input type="text" value={formData.passportPlaceOfIssue} onChange={(e) => set('passportPlaceOfIssue', e.target.value)} className={inputCls} /></Field>
                  <Field label="Date of Expiry"><input type="date" value={formData.passportDateOfExpiry} onChange={(e) => set('passportDateOfExpiry', e.target.value)} className={inputCls} /></Field>
                  <Field label="Driving License No"><input type="text" value={formData.drivingLicenseNo} onChange={(e) => set('drivingLicenseNo', e.target.value)} className={inputCls} /></Field>
                </div>
              )}

              {/* TRAVEL & BANKING */}
              <SectionHeader title="Travel & Banking" open={openSections.travel} toggle={() => toggleSection('travel')} />
              {openSections.travel && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <Field label="Mode of Travel">
                    <select value={formData.modeOfTravel} onChange={(e) => set('modeOfTravel', e.target.value)} className={selectCls}>
                      <option value="">Select</option>
                      <option value="Own Vehicle">Own Vehicle</option>
                      <option value="Public Transport">Public Transport</option>
                      <option value="Company Vehicle">Company Vehicle</option>
                      <option value="Walking">Walking</option>
                    </select>
                  </Field>
                  <Field label="2 Wheeler / 4 Wheeler Reg No"><input type="text" value={formData.vehicleRegNo} onChange={(e) => set('vehicleRegNo', e.target.value)} className={inputCls} /></Field>
                  <Field label="Bank Account No"><input type="text" value={formData.bankAccountNo} onChange={(e) => set('bankAccountNo', e.target.value)} className={inputCls} /></Field>
                  <Field label="IFSC Code"><input type="text" value={formData.ifscCode} onChange={(e) => set('ifscCode', e.target.value)} className={inputCls} /></Field>
                  <Field label="ESI No"><input type="text" value={formData.esiNo} onChange={(e) => set('esiNo', e.target.value)} className={inputCls} /></Field>
                  <Field label="EPF No"><input type="text" value={formData.epfNo} onChange={(e) => set('epfNo', e.target.value)} className={inputCls} /></Field>
                </div>
              )}

              {/* PREVIOUS EMPLOYMENT */}
              <SectionHeader title="Previous Employment & Education" open={openSections.prevEmployment} toggle={() => toggleSection('prevEmployment')} />
              {openSections.prevEmployment && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <Field label="Last Employer"><input type="text" value={formData.lastEmployer} onChange={(e) => set('lastEmployer', e.target.value)} className={inputCls} /></Field>
                  <Field label="Contact Person"><input type="text" value={formData.lastEmployerContact} onChange={(e) => set('lastEmployerContact', e.target.value)} className={inputCls} /></Field>
                  <Field label="Last Gross Salary"><input type="number" value={formData.lastGrossSalary} onChange={(e) => set('lastGrossSalary', e.target.value)} className={inputCls} /></Field>
                  <Field label="Last Qualification"><input type="text" value={formData.lastQualification} onChange={(e) => set('lastQualification', e.target.value)} className={inputCls} /></Field>
                  <Field label="Last Educational Institution"><input type="text" value={formData.lastEducationalInstitution} onChange={(e) => set('lastEducationalInstitution', e.target.value)} className={inputCls} /></Field>
                  <Field label="Year of Graduation"><input type="text" value={formData.yearOfGraduation} onChange={(e) => set('yearOfGraduation', e.target.value)} placeholder="e.g., 2020" className={inputCls} /></Field>
                </div>
              )}

              {/* FAMILY DETAILS */}
              <SectionHeader title="Family Details" open={openSections.family} toggle={() => toggleSection('family')} />
              {openSections.family && (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-3">
                    <Field label="Father's Name"><input type="text" value={formData.fatherName} onChange={(e) => set('fatherName', e.target.value)} className={inputCls} /></Field>
                    <Field label="Father's Address"><input type="text" value={formData.fatherAddress} onChange={(e) => set('fatherAddress', e.target.value)} className={inputCls} /></Field>
                    <Field label="Father's Contact"><input type="tel" value={formData.fatherContact} onChange={(e) => set('fatherContact', e.target.value)} className={inputCls} /></Field>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <Field label="Mother's Name"><input type="text" value={formData.motherName} onChange={(e) => set('motherName', e.target.value)} className={inputCls} /></Field>
                    <Field label="Mother's Address"><input type="text" value={formData.motherAddress} onChange={(e) => set('motherAddress', e.target.value)} className={inputCls} /></Field>
                    <Field label="Mother's Contact"><input type="tel" value={formData.motherContact} onChange={(e) => set('motherContact', e.target.value)} className={inputCls} /></Field>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <Field label="Married">
                      <select value={formData.married} onChange={(e) => set('married', e.target.value)} className={selectCls}>
                        <option value="No">No</option>
                        <option value="Yes">Yes</option>
                      </select>
                    </Field>
                    {formData.married === 'Yes' && (
                      <>
                        <Field label="Spouse Name"><input type="text" value={formData.spouseName} onChange={(e) => set('spouseName', e.target.value)} className={inputCls} /></Field>
                        <Field label="Spouse Contact"><input type="tel" value={formData.spouseContact} onChange={(e) => set('spouseContact', e.target.value)} className={inputCls} /></Field>
                      </>
                    )}
                  </div>
                  {formData.married === 'Yes' && (
                    <div className="grid grid-cols-3 gap-3">
                      <Field label="Spouse Address"><input type="text" value={formData.spouseAddress} onChange={(e) => set('spouseAddress', e.target.value)} className={inputCls} /></Field>
                    </div>
                  )}
                </div>
              )}

              {/* HEALTH & EMERGENCY */}
              <SectionHeader title="Health & Emergency" open={openSections.health} toggle={() => toggleSection('health')} />
              {openSections.health && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Field label="Blood Group">
                    <select value={formData.bloodGroup} onChange={(e) => set('bloodGroup', e.target.value)} className={selectCls} data-testid="add-emp-blood">
                      <option value="">Select</option>
                      {bloodGroups.map(bg => <option key={bg} value={bg}>{bg}</option>)}
                    </select>
                  </Field>
                  <Field label="Any Known Allergies"><input type="text" value={formData.knownAllergies} onChange={(e) => set('knownAllergies', e.target.value)} className={inputCls} /></Field>
                  <Field label="Emergency Contact"><input type="text" value={formData.emergencyContact} onChange={(e) => set('emergencyContact', e.target.value)} className={inputCls} /></Field>
                  <Field label="Emergency Contact Number"><input type="tel" value={formData.emergencyContactNumber} onChange={(e) => set('emergencyContactNumber', e.target.value)} className={inputCls} /></Field>
                </div>
              )}

              {/* REFERENCE */}
              <SectionHeader title="Reference" open={openSections.reference} toggle={() => toggleSection('reference')} />
              {openSections.reference && (
                <div>
                  <Field label="Any Reference"><input type="text" value={formData.reference} onChange={(e) => set('reference', e.target.value)} placeholder="Reference person name & contact" className={inputCls} /></Field>
                </div>
              )}

              {/* SALARY BREAKUP */}
              <SectionHeader title="Salary Breakup" open={openSections.salary} toggle={() => toggleSection('salary')} />
              {openSections.salary && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <Field label="Basic (Monthly)"><input type="number" value={formData.salaryBasic} onChange={(e) => set('salaryBasic', e.target.value)} placeholder="0" className={inputCls} /></Field>
                    <Field label="HRA (Monthly)"><input type="number" value={formData.salaryHRA} onChange={(e) => set('salaryHRA', e.target.value)} placeholder="0" className={inputCls} /></Field>
                    <Field label="DA (Monthly)"><input type="number" value={formData.salaryDA} onChange={(e) => set('salaryDA', e.target.value)} placeholder="0" className={inputCls} /></Field>
                    <Field label="Other Allowance (Monthly)"><input type="number" value={formData.salaryOtherAllowance} onChange={(e) => set('salaryOtherAllowance', e.target.value)} placeholder="0" className={inputCls} /></Field>
                    <Field label="Incentive (Monthly)"><input type="number" value={formData.salaryIncentive} onChange={(e) => set('salaryIncentive', e.target.value)} placeholder="0" className={inputCls} /></Field>
                  </div>
                  <div className="grid grid-cols-3 gap-3 bg-blue-50 p-3 rounded-lg border border-blue-200">
                    <Field label="Bonus (8.33% of Basic+HRA+DA+OthAll)"><div className={readOnlyCls}>{salaryCalc.bonus.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div></Field>
                    <Field label="Gross Per Month (auto)"><div className={`${readOnlyCls} font-bold text-green-700`}>{salaryCalc.grossMonth.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div></Field>
                    <Field label="Gross Per Annum (auto)"><div className={`${readOnlyCls} font-bold text-[#1a237e]`}>{salaryCalc.grossAnnum.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div></Field>
                  </div>
                </div>
              )}

              {/* DOCUMENT UPLOADS */}
              <SectionHeader title="Upload Documents" open={openSections.uploads} toggle={() => toggleSection('uploads')} />
              {openSections.uploads && (
                <div className="space-y-3">
                  <p className="text-xs text-gray-500">Upload employee documents (Aadhaar, PAN, Passport, Certificates, etc.). Max 10MB per file.</p>
                  <div className="flex items-center gap-3">
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                      onChange={handleFileUpload}
                      className="hidden"
                      data-testid="doc-upload-input"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-[#1a237e] hover:text-[#1a237e] transition"
                      data-testid="doc-upload-btn"
                    >
                      <Upload size={16} />
                      {uploading ? 'Uploading...' : 'Choose Files'}
                    </button>
                    {uploadedDocs.length > 0 && (
                      <span className="text-xs text-gray-500">{uploadedDocs.length} file(s) attached</span>
                    )}
                  </div>
                  {uploadedDocs.length > 0 && (
                    <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                      {uploadedDocs.map((doc) => (
                        <div key={doc.id} className="flex items-center justify-between px-3 py-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText size={16} className="text-blue-500 flex-shrink-0" />
                            <span className="text-sm text-gray-800 truncate">{doc.name}</span>
                            <span className="text-xs text-gray-400 flex-shrink-0">({formatSize(doc.size)})</span>
                          </div>
                          <button type="button" onClick={() => removeDoc(doc.id)} className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition flex-shrink-0">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button type="button" onClick={() => { setShowAddModal(false); setFormData({ ...INITIAL_FORM }); setUploadedDocs([]); setEditMode(false); setEditingEmployeeId(null); }}
                  className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50" data-testid="cancel-add-employee">Cancel</button>
                <button type="submit" className="px-5 py-2 bg-[#1a237e] text-white rounded-lg hover:bg-[#283593]" data-testid="submit-add-employee">{editMode ? 'Update Employee' : 'Add Employee'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===== EMPLOYEE DETAIL MODAL ===== */}
      {showDetailModal && selectedEmployee && (
        <DetailModal employee={selectedEmployee} onClose={() => setShowDetailModal(false)} />
      )}
    </div>
  );
}

function DetailModal({ employee: e, onClose }) {
  const DetailRow = ({ label, value }) => value ? (
    <div><span className="text-gray-500 text-xs">{label}</span><p className="font-medium text-sm">{value}</p></div>
  ) : null;

  const Section = ({ title, children }) => {
    const childArray = Array.isArray(children) ? children : [children];
    const hasContent = childArray.filter(Boolean).length > 0;
    if (!hasContent) return null;
    return (
      <div>
        <h3 className="text-sm font-bold text-[#1a237e] uppercase tracking-wide border-b border-gray-200 pb-1 mb-3">{title}</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">{children}</div>
      </div>
    );
  };

  const bonus = (parseFloat(e.salaryBasic || 0) + parseFloat(e.salaryHRA || 0) + parseFloat(e.salaryDA || 0) + parseFloat(e.salaryOtherAllowance || 0)) * 0.0833;
  const grossMonth = (parseFloat(e.salaryBasic || 0) + parseFloat(e.salaryHRA || 0) + parseFloat(e.salaryDA || 0) + parseFloat(e.salaryOtherAllowance || 0) + parseFloat(e.salaryIncentive || 0) + bonus);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-[#1a237e] to-[#283593] p-6 text-white flex justify-between items-start z-10">
          <div className="flex items-center gap-4">
            {e.photo ? (
              <img src={e.photo} alt={e.name} className="h-16 w-16 rounded-full object-cover border-2 border-white/30" />
            ) : (
              <div className="h-16 w-16 bg-white/20 rounded-full flex items-center justify-center text-2xl font-bold">
                {e.name?.charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <h2 className="text-xl font-bold">{e.name}</h2>
              <p className="text-white/80">{e.designation} - {e.department}</p>
              <p className="text-sm text-white/60 mt-1">{e.employeeId || e.id}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-6">
          <Section title="Personal Information">
            <DetailRow label="Gender" value={e.gender} />
            <DetailRow label="Date of Birth" value={e.dateOfBirth} />
            <DetailRow label="Place of Birth" value={e.placeOfBirth} />
            <DetailRow label="Date of Joining" value={e.joinDate} />
            <DetailRow label="Designation" value={e.designation} />
            <DetailRow label="Department" value={e.department} />
            <DetailRow label="Function" value={e.function} />
            <DetailRow label="Role" value={e.role} />
            <DetailRow label="Identification Mark" value={e.identificationMark} />
            <DetailRow label="Status" value={e.status} />
          </Section>
          <Section title="Contact Information">
            <DetailRow label="Email" value={e.email} />
            <DetailRow label="Mobile" value={e.mobile} />
            <DetailRow label="Telephone" value={e.telephone} />
            <DetailRow label="Current Address" value={e.currentAddress} />
            <DetailRow label="Permanent Address" value={e.permanentAddress} />
            <DetailRow label="Address Phone" value={e.addressPhone} />
          </Section>
          <Section title="Documents">
            <DetailRow label="Passport No" value={e.passportNo} />
            <DetailRow label="Place of Issue" value={e.passportPlaceOfIssue} />
            <DetailRow label="Date of Expiry" value={e.passportDateOfExpiry} />
            <DetailRow label="Driving License No" value={e.drivingLicenseNo} />
          </Section>
          <Section title="Travel & Banking">
            <DetailRow label="Mode of Travel" value={e.modeOfTravel} />
            <DetailRow label="Vehicle Reg No" value={e.vehicleRegNo} />
            <DetailRow label="Bank Account No" value={e.bankAccountNo} />
            <DetailRow label="IFSC Code" value={e.ifscCode} />
            <DetailRow label="ESI No" value={e.esiNo} />
            <DetailRow label="EPF No" value={e.epfNo} />
          </Section>
          <Section title="Previous Employment & Education">
            <DetailRow label="Last Employer" value={e.lastEmployer} />
            <DetailRow label="Contact Person" value={e.lastEmployerContact} />
            <DetailRow label="Last Gross Salary" value={e.lastGrossSalary ? `Rs. ${e.lastGrossSalary.toLocaleString('en-IN')}` : null} />
            <DetailRow label="Qualification" value={e.lastQualification} />
            <DetailRow label="Educational Institution" value={e.lastEducationalInstitution} />
            <DetailRow label="Year of Graduation" value={e.yearOfGraduation} />
          </Section>
          <Section title="Family Details">
            <DetailRow label="Father's Name" value={e.fatherName} />
            <DetailRow label="Father's Address" value={e.fatherAddress} />
            <DetailRow label="Father's Contact" value={e.fatherContact} />
            <DetailRow label="Mother's Name" value={e.motherName} />
            <DetailRow label="Mother's Address" value={e.motherAddress} />
            <DetailRow label="Mother's Contact" value={e.motherContact} />
            <DetailRow label="Married" value={e.married} />
            {e.married === 'Yes' && <DetailRow label="Spouse Name" value={e.spouseName} />}
            {e.married === 'Yes' && <DetailRow label="Spouse Address" value={e.spouseAddress} />}
            {e.married === 'Yes' && <DetailRow label="Spouse Contact" value={e.spouseContact} />}
          </Section>
          <Section title="Health & Emergency">
            <DetailRow label="Blood Group" value={e.bloodGroup} />
            <DetailRow label="Known Allergies" value={e.knownAllergies} />
            <DetailRow label="Emergency Contact" value={e.emergencyContact} />
            <DetailRow label="Emergency Contact Number" value={e.emergencyContactNumber} />
          </Section>
          {e.reference && (
            <Section title="Reference">
              <DetailRow label="Reference" value={e.reference} />
            </Section>
          )}
          {(e.salaryBasic > 0 || e.salaryHRA > 0 || e.salaryDA > 0 || e.salaryOtherAllowance > 0 || e.salaryIncentive > 0) && (
            <div>
              <h3 className="text-sm font-bold text-[#1a237e] uppercase tracking-wide border-b border-gray-200 pb-1 mb-3">Salary Breakup</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <DetailRow label="Basic / Month" value={e.salaryBasic ? `Rs. ${e.salaryBasic.toLocaleString('en-IN')}` : null} />
                <DetailRow label="HRA / Month" value={e.salaryHRA ? `Rs. ${e.salaryHRA.toLocaleString('en-IN')}` : null} />
                <DetailRow label="DA / Month" value={e.salaryDA ? `Rs. ${e.salaryDA.toLocaleString('en-IN')}` : null} />
                <DetailRow label="Other Allowance / Month" value={e.salaryOtherAllowance ? `Rs. ${e.salaryOtherAllowance.toLocaleString('en-IN')}` : null} />
                <DetailRow label="Incentive / Month" value={e.salaryIncentive ? `Rs. ${e.salaryIncentive.toLocaleString('en-IN')}` : null} />
              </div>
              <div className="grid grid-cols-3 gap-3 mt-3 bg-blue-50 p-3 rounded-lg border border-blue-200">
                <div><span className="text-gray-500 text-xs">Bonus (8.33%)</span><p className="font-medium text-sm">Rs. {bonus.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</p></div>
                <div><span className="text-gray-500 text-xs">Gross Per Month</span><p className="font-bold text-sm text-green-700">Rs. {grossMonth.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</p></div>
                <div><span className="text-gray-500 text-xs">Gross Per Annum</span><p className="font-bold text-sm text-[#1a237e]">Rs. {(grossMonth * 12).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</p></div>
              </div>
            </div>
          )}
          {e.documents && e.documents.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-[#1a237e] uppercase tracking-wide border-b border-gray-200 pb-1 mb-3">Uploaded Documents</h3>
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                {e.documents.map((doc, idx) => (
                  <div key={idx} className="flex items-center justify-between px-3 py-2">
                    <div className="flex items-center gap-2">
                      <FileText size={16} className="text-blue-500" />
                      <a href={doc.data} download={doc.name} className="text-sm text-blue-600 hover:underline">{doc.name}</a>
                      <span className="text-xs text-gray-400">({doc.size < 1024*1024 ? (doc.size/1024).toFixed(1)+' KB' : (doc.size/(1024*1024)).toFixed(1)+' MB'})</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MasterRoll;
